import { ColumnDef } from "@tanstack/react-table";
import { formatMoney } from "@/core/utils/money";
import type { IcmPieTab } from "./common";
import type {
  IcmJob,
  IcmLineItem,
  IcmPayoutBreakdownEntry,
  IcmPayoutDisplayType,
} from "./types";

export { humanizeKey } from "./labels";

const DEFAULT_COLUMN_WIDTH = 120;
export const DEFAULT_PAYOUT_DISPLAY_TYPE = "currency" as const;
const NUMBER_FORMATTER = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
});
const PERCENT_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const formatPlainNumber = (value: number | string): string => {
  const num = Number(value);

  if (Number.isNaN(num)) return String(value);

  return NUMBER_FORMATTER.format(num);
};

export const normalizePayoutRuleName = (value: string | null | undefined) => {
  if (!value) return "Unknown";

  const normalized = value.normalize("NFKC").replace(/\s+/g, " ").trim();
  if (!normalized) return "Unknown";

  const cleaned = normalized
    .replace(/\s*\(?v\d+\)?$/i, "")
    .replace(/[\s\-_:()]+$/g, "")
    .trim();

  return cleaned || normalized || "Unknown";
};

export const formatValueByDisplayType = (
  value: unknown,
  displayType: IcmPayoutDisplayType = DEFAULT_PAYOUT_DISPLAY_TYPE,
): string => {
  if (value === null || value === undefined || value === "") return "–";

  switch (displayType) {
    case "number":
      return formatPlainNumber(value as number | string);
    case "percent": {
      const num = Number(value);
      return Number.isNaN(num) ? String(value) : PERCENT_FORMATTER.format(num);
    }
    case "text":
      return String(value);
    case "currency":
    default: {
      const formatted = formatMoney(value as number | string | null);
      return formatted ?? String(value);
    }
  }
};

export const getPayoutBreakdownEntryValue = (
  entry: IcmPayoutBreakdownEntry,
): number | string | null =>
  typeof entry === "object" && entry !== null && "value" in entry
    ? entry.value
    : entry;

export const getPayoutBreakdownEntryDisplayType = (
  entry: IcmPayoutBreakdownEntry,
): IcmPayoutDisplayType =>
  typeof entry === "object" && entry !== null && "display_type" in entry
    ? (entry.display_type ?? DEFAULT_PAYOUT_DISPLAY_TYPE)
    : DEFAULT_PAYOUT_DISPLAY_TYPE;

export const formatPayoutBreakdownValue = (
  entry: IcmPayoutBreakdownEntry,
): string =>
  formatValueByDisplayType(
    getPayoutBreakdownEntryValue(entry),
    getPayoutBreakdownEntryDisplayType(entry),
  );

export const formatTraceValue = (key: string, value: unknown): string => {
  if (value === null || value === undefined) return "–";
  if (typeof value === "boolean") return value ? "Yes" : "No";

  const k = key.toLowerCase();
  const num = Number(value);

  if (!Number.isNaN(num) && value !== "") {
    if (k.endsWith("_pct") || k === "split_pct") {
      return `${(num * 100).toFixed(0)}%`;
    }

    if (k.endsWith("_rate")) {
      return num <= 1.0
        ? `${(num * 100).toFixed(1)}%`
        : (formatMoney(String(value)) ?? "–");
    }

    if (
      k.includes("amount") ||
      k.includes("subtotal") ||
      k.includes("total") ||
      k.endsWith("_pay") ||
      k.endsWith("_payout") ||
      k.endsWith("_price") ||
      k === "price" ||
      k === "commission" ||
      k.endsWith("_commission")
    ) {
      return formatMoney(String(value)) ?? "–";
    }

    return parseFloat(num.toFixed(4)).toString();
  }

  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const toPieEntries = (map: Record<string, number>) =>
  Object.entries(map).map(([name, value]) => ({
    name,
    value: parseFloat(value.toFixed(2)),
  }));

export const buildJobsPieChartTabs = (jobs: IcmJob[]): IcmPieTab[] => {
  if (!jobs.length) return [];

  const byRule = jobs
    .flatMap((j) => j.payouts)
    .reduce<Record<string, number>>((acc, payout) => {
      const key = normalizePayoutRuleName(payout.payout_rule_name);
      acc[key] = (acc[key] ?? 0) + parseFloat(payout.payout_amount);
      return acc;
    }, {});

  const byBusinessUnit = jobs.reduce<Record<string, number>>((acc, job) => {
    const key = job.business_unit_name ?? "Unknown";
    acc[key] = (acc[key] ?? 0) + parseFloat(job.total_payout);
    return acc;
  }, {});

  return [
    { label: "Payout Rule", data: toPieEntries(byRule) },
    { label: "Business unit", data: toPieEntries(byBusinessUnit) },
  ];
};

export const buildLineItemsPieChartTabs = (
  items: IcmLineItem[],
): IcmPieTab[] => {
  if (!items.length) return [];

  const byRule = items.reduce<Record<string, number>>((acc, item) => {
    const key = normalizePayoutRuleName(item.payout_rule_name);
    acc[key] = (acc[key] ?? 0) + parseFloat(item.payout_amount);
    return acc;
  }, {});

  const byJobType = items.reduce<Record<string, number>>((acc, item) => {
    const key = item.job_type_name ?? "Unknown";
    acc[key] = (acc[key] ?? 0) + parseFloat(item.payout_amount);
    return acc;
  }, {});

  return [
    { label: "Payout Rule", data: toPieEntries(byRule) },
    { label: "Job Type", data: toPieEntries(byJobType) },
  ];
};

export const getTableMinWidth = <TData>(columns: ColumnDef<TData, unknown>[]) =>
  columns.reduce((sum, column) => {
    const size = (column as { meta?: { size?: string } }).meta?.size;
    const px = size ? parseInt(size, 10) : DEFAULT_COLUMN_WIDTH;
    return sum + (Number.isNaN(px) ? DEFAULT_COLUMN_WIDTH : px);
  }, 0);
